/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The settings that open one at a time inside a tab.
 *
 * Imóveis, Vendas and Locações each grew a stack of blocks that only got
 * longer, so they now open on a list with one row per setting and its current
 * value, and a row opens that setting alone. Adding a setting is one entry
 * here plus its block.
 */

import type { SettingsSection } from './settings-nav';

export type SettingsItemKey =
  | 'propertyRequired'
  | 'saleCommission' | 'saleMilestones' | 'saleCategories'
  | 'leaseDueDate' | 'leaseCategories';

/** Where the settings page is: a tab, and the setting open in it, if any. */
export interface SettingsTarget {
  section: SettingsSection;
  item: SettingsItemKey | null;
}

/** The items of each tab that has them, in the order the list shows them. */
const SECTION_ITEMS: Partial<Record<SettingsSection, SettingsItemKey[]>> = {
  imoveis: ['propertyRequired'],
  vendas: ['saleCommission', 'saleMilestones', 'saleCategories'],
  locacoes: ['leaseDueDate', 'leaseCategories'],
};

/**
 * The items a tab lists; empty for a tab that is one screen.
 *
 * @param section - The tab.
 * @returns Its items in display order.
 */
export function itemsOf(section: SettingsSection): SettingsItemKey[] {
  return SECTION_ITEMS[section] ?? [];
}

/**
 * Reads an item from the URL, keeping it only when it belongs to the tab, so
 * a stale or hand-edited link falls back to the list instead of a blank page.
 *
 * @param section - The tab the URL opens.
 * @param raw - The `item` query value.
 * @returns The item, or null for the list.
 */
export function itemFor(section: SettingsSection, raw: string | null): SettingsItemKey | null {
  const items = itemsOf(section);
  return items.find((item) => item === raw) ?? null;
}

/** One thing the search can take the user to. */
export interface SettingsSearchEntry {
  target: SettingsTarget;
  title: string;
  /** Where it lives, or what it is about: shown under the title. */
  context: string;
  /** Other words people use for it; matched, never shown. */
  keywords: string;
}

const MAX_RESULTS = 8;

/**
 * Lowercases and drops accents, so "comissao" finds "Comissão".
 *
 * @param text - Any text.
 * @returns The text as the search compares it.
 */
export function searchable(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * The entries whose title, context or keywords contain the query.
 *
 * @param entries - Everything the user may open.
 * @param query - What the user typed.
 * @returns At most eight matches, in catalogue order; none for a blank query.
 */
export function matchSettings(entries: SettingsSearchEntry[], query: string): SettingsSearchEntry[] {
  const wanted = searchable(query.trim());
  if (!wanted) return [];
  return entries
    .filter((entry) => searchable(`${entry.title} ${entry.context} ${entry.keywords}`).includes(wanted))
    .slice(0, MAX_RESULTS);
}
